/**
 * Pure helpers behind the Notification Setup grid's staged edits: comparing
 * configs, pruning the staged set after a partial save, and turning one row
 * into the override payload the API speaks.
 *
 * @link ./useNotificationSetup for the hook that stages and saves.
 */
import type { NotificationChannel, NotificationOverrideInput } from '../../../../lib/api';
import { canTurnOn } from './eventState';
import type { EventConfig, SetupEvent } from './types';

/**
 * Whether two channel lists are the same.
 *
 * Channel lists are stored in a fixed order, so a join comparison is exact.
 *
 * @param a - one channel list.
 * @param b - the other.
 * @returns true when both name the same channels.
 */
export const sameChannels = (a: NotificationChannel[], b: NotificationChannel[]): boolean =>
  a.join('|') === b.join('|');

/**
 * Whether two configs would save the same thing.
 *
 * @param a - one config.
 * @param b - the other.
 * @returns true when enabled state, cadence and channels all match.
 */
export const sameConfig = (a: EventConfig, b: EventConfig): boolean =>
  a.enabled === b.enabled
  && a.frequency === b.frequency
  && sameChannels(a.channels, b.channels);

/**
 * Keep only the staged rows named.
 *
 * Used after a partial save: the rows the server accepted are dropped (the
 * reload brings back server truth for them) while every rejected row stays
 * exactly as the admin left it.
 *
 * @param current - the staged map.
 * @param keys - event keys to keep.
 * @returns a new map holding only those keys.
 */
export const retainKeys = <T>(current: Record<string, T>, keys: string[]): Record<string, T> => {
  if (!keys.length) return {};
  const kept: Record<string, T> = {};
  for (const key of keys) {
    const value = current[key];
    if (value !== undefined) kept[key] = value;
  }
  return kept;
};

/**
 * One row's write payload. Anything matching the shipped default goes out as
 * its inherit marker, so an override row only ever holds real differences —
 * and a row where everything matches is deleted by the API.
 *
 * Every payload carries the scope claim, so a row can only ever be written to
 * the company the header is naming.
 *
 * @param event - the grid row being written.
 * @param next - its staged config, or `null` when the row is being reset.
 * @param scopeClaim - `{ company }` from the catalogue's scope block, or `{}`.
 * @returns the `POST /template-overrides/` body.
 */
export function buildOverrideInput(
  event: SetupEvent,
  next: EventConfig | null,
  scopeClaim: { company?: number },
): NotificationOverrideInput {
  if (next === null) {
    return {
      ...scopeClaim,
      template_key: event.key,
      is_enabled: null,
      channel_override: null,
      frequency: null,
      dedup_window_override: null,
      audience_rule_override: null,
    };
  }
  // Clamp to the shipped channels at the payload boundary. The API refuses
  // any channel the platform does not ship this event on
  // (`channel_widening`), so a widened list can never leave the screen —
  // whatever a stale catalogue or a bulk action staged.
  const channels = event.availableChannels.filter((channel) =>
    next.channels.includes(channel),
  );
  // The same boundary for a retired event: the API refuses to switch it on
  // (`event_retired`), so `true` never leaves the screen for one.
  const enabled = next.enabled && canTurnOn(event);
  return {
    ...scopeClaim,
    template_key: event.key,
    is_enabled: enabled === event.defaults.enabled ? null : enabled,
    channel_override: sameChannels(channels, event.defaults.channels)
      ? null
      : channels,
    // Already null when the row inherits, which is the API's own inherit
    // marker — an inheriting row never pins a cadence it would then be
    // stuck on the next time the tenant default moves.
    frequency: next.frequency,
  };
}
